import dayjs from "dayjs"

import type { PortalProjectPhase } from "../types/portal.types.ts"

// Mirrors apps.core.models.ProjectPhase.StatusChoices (ezbuild_back).
const COMPLETED_STATUS = 3

export type MilestoneScheduleState = "completed" | "current" | "upcoming" | "overdue"

export interface EvaluatedMilestone {
  phase: PortalProjectPhase
  /** The date Home/Project display for this milestone — end first, start as fallback. */
  displayDate: string | null
  state: MilestoneScheduleState
  /** True when an incomplete milestone's display date has already passed —
   * set on `current` and `overdue` alike, so a slipping current phase can
   * still be flagged without moving it out of "current work". */
  isPastDue: boolean
}

/**
 * One shared classification for every screen that shows milestones (Home,
 * Project — "use one consistent calculation").
 *
 * Four states, not two, is what keeps "Current work" and "Next milestone"
 * from ever showing the same item:
 * - `completed` once finished, regardless of date.
 * - `current`: already started (`is_started`) and not finished — this is
 *   "Current work". A project can genuinely have more than one of these
 *   (parallel milestones across project types); no single "the" current
 *   phase is invented.
 * - `upcoming`: not yet started, not finished, dated today-or-later (or
 *   undated) — this is where "Next milestone" is drawn from, so it can
 *   never repeat whatever is already `current`.
 * - `overdue`: not yet started, not finished, and already past its date —
 *   surfaced as "Schedule update needed", never silently read as upcoming.
 */
export const evaluateMilestone = (
  phase: PortalProjectPhase,
  today: dayjs.Dayjs
): EvaluatedMilestone => {
  const displayDate = phase.end_date || phase.start_date || null
  const isCompleted = phase.is_finished || phase.status === COMPLETED_STATUS
  const isPastDue = !isCompleted && !!displayDate && dayjs(displayDate).isBefore(today, "day")

  if (isCompleted) {
    return { phase, displayDate, state: "completed", isPastDue: false }
  }
  if (phase.is_started) {
    return { phase, displayDate, state: "current", isPastDue }
  }
  return { phase, displayDate, state: isPastDue ? "overdue" : "upcoming", isPastDue }
}

const byDisplayDateAsc = (a: EvaluatedMilestone, b: EvaluatedMilestone) =>
  (a.displayDate || "").localeCompare(b.displayDate || "")

export interface MilestoneSchedule {
  /** Already started, not finished — "Current work". Zero, one or several. */
  current: EvaluatedMilestone[]
  /** Not yet started, dated today-or-later (or undated) — genuine forward work. */
  upcoming: EvaluatedMilestone[]
  /** Not yet started, dated before today — needs a schedule update. */
  overdue: EvaluatedMilestone[]
  /** Earliest `upcoming` milestone — never one already in `current`. */
  next: EvaluatedMilestone | null
}

export const buildMilestoneSchedule = (
  phases: PortalProjectPhase[],
  today: dayjs.Dayjs = dayjs()
): MilestoneSchedule => {
  const evaluated = phases.map((phase) => evaluateMilestone(phase, today))
  const current = evaluated.filter((m) => m.state === "current").sort(byDisplayDateAsc)
  const upcoming = evaluated.filter((m) => m.state === "upcoming").sort(byDisplayDateAsc)
  const overdue = evaluated.filter((m) => m.state === "overdue").sort(byDisplayDateAsc)

  return { current, upcoming, overdue, next: upcoming[0] ?? null }
}
